import { alertApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  Badge,
  Box,
  capitalize,
  makeStyles,
  Slide,
  SlideProps,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';
import { Alert } from '@material-ui/lab';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CoordinatorError,
  CustomError,
  CustomErrorName,
} from '@agilelab/plugin-wb-platform-common';
import { customAlertApiRef } from '../../apis';
import { CustomAlertMessage } from '../../apis/AlertApi/CustomAlertApi';
import { CustomAlertDetails } from './CustomAlertDetails';
import { WbButton } from '../WbButton';

/**
 * Displays alerts from {@link @mainstage/core-plugin-api#AlertApi}
 * with respect to the original backstage AlertApi,
 * and custom alerts from CustomAlertApi.
 *
 * @public
 * @remarks
 *
 * Shown as SnackBar at the top of the page
 */
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const useStyles = makeStyles(
  theme => ({
    snackbar: {
      minWidth: '20%',
      maxWidth: '100%',
      zIndex: 1500,
    },
    alert: {
      width: '100%',
    },
    title: {
      overflowWrap: 'anywhere',
      display: 'flex',
      gap: '8px',
      alignItems: 'start',
      flexDirection: 'column',
    },
    alertContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    detailsLink: {
      marginTop: theme.spacing(2),
      color: theme.palette.error.main,
      textDecoration: 'underline',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    subMessage: {
      whiteSpace: 'pre-line',
    },
    moreInfoError: {
      marginTop: theme.spacing(2),
    },
  }),
  { name: 'CustomAlertDisplayName' },
);

const FOCUSABLE_SELECTORS =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Returns all visible focusable elements found in `root`.
 */
function getFocusableElements(
  root: ParentNode,
  exclude?: HTMLElement | null,
): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  ).filter(el => {
    if (el === exclude) return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0;
  });
}

/**
 * Returns the next focusable element in the global DOM after `from`,
 * excluding any element inside `excludeRoot`.
 */
function getNextFocusableOutsideRoot(
  from: HTMLElement,
  excludeRoot?: HTMLElement | null,
): HTMLElement | null {
  const all = getFocusableElements(document, undefined).filter(
    el => !excludeRoot?.contains(el),
  );

  const idx = all.indexOf(from);
  return idx !== -1 ? all[idx + 1] ?? null : null;
}

function handleMultipleAlerts(alert: JSX.Element, length: number): JSX.Element {
  return length > 1 ? (
    <Badge
      badgeContent={`${length - 1} more`}
      color="primary"
      style={{ width: '100%' }}
    >
      {alert}
    </Badge>
  ) : (
    alert
  );
}
export function CustomAlertDisplay(_props: {}) {
  const [messages, setMessages] = useState<Array<CustomAlertMessage>>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [moreInfoCustomError, setMoreInfoCustomError] = useState<
    string | undefined
  >();

  const alertApi = useApi(alertApiRef);
  const customAlertApi = useApi(customAlertApiRef);
  const classes = useStyles();

  /**
   * Ref to the close (X) button rendered inside the Alert.
   */
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  /**
   * Ref to the Snackbar root used to detect focus events inside the alert.
   */
  const snackbarRootRef = useRef<HTMLDivElement>(null);
  /**
   * The element that was focused when the first alert opened.
   * Saved only once per alert session (Caso 1, 3, 4).
   */
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  /**
   * Prevents lastFocusedRef from being overwritten when more alerts are added.
   */
  const hasSavedFocusRef = useRef(false);

  /**
   * Always tracks the last meaningfully focused element outside the alert.
   * Updated continuously so it holds the correct value even when alerts are
   * emitted asynchronously (e.g. after an API call).
   */
  const prevFocusedRef = useRef<HTMLElement | null>(null);
  /**
   * Tracks whether the initial body→alert focus redirect has already fired,
   * so it only happens once per alert session.
   */
  const hasRedirectedFromBodyRef = useRef(false);

  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const el = event.target as HTMLElement | null;
      if (!el || el === document.body) return;
      // Ignore elements inside the snackbar so navigating within the alert
      // does not overwrite the original trigger element.
      if (snackbarRootRef.current?.contains(el)) return;
      prevFocusedRef.current = el;
    };
    document.addEventListener('focusin', onFocusIn, true);
    return () => document.removeEventListener('focusin', onFocusIn, true);
  }, []);

  useEffect(() => {
    const subscription = alertApi.alert$().subscribe(message => {
      // Capture focus synchronously before React re-renders and the Snackbar appears.
      // At this point document.activeElement still reflects the element the user was on.
      if (!hasSavedFocusRef.current) {
        const active = prevFocusedRef.current;
        if (active) {
          lastFocusedRef.current = active;
        }
        hasSavedFocusRef.current = true;
      }
      setMessages(msgs => [message, ...msgs]);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [alertApi]);

  useEffect(() => {
    const subscription = customAlertApi.alert$().subscribe(message => {
      // Same as above: use the continuously tracked prev focus.
      if (!hasSavedFocusRef.current) {
        const active = prevFocusedRef.current;
        if (active) {
          lastFocusedRef.current = active;
        }
        hasSavedFocusRef.current = true;
      }
      setMessages(msgs => [message, ...msgs]);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [customAlertApi]);

  useEffect(() => {
    // All alerts dismissed: reset all focus state
    if (messages.length === 0) {
      lastFocusedRef.current = null;
      hasSavedFocusRef.current = false;
      hasRedirectedFromBodyRef.current = false;
      return undefined;
    }

    // Caso 2: no focused element when alert opened → do not intercept Tab
    if (!lastFocusedRef.current) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const closeBtn = closeButtonRef.current;
      const snackbarRoot = snackbarRootRef.current;
      // In capture phase document.activeElement still reflects current focus.
      const active = document.activeElement as HTMLElement | null;
      const alertFocusableElements = snackbarRoot
        ? getFocusableElements(snackbarRoot)
        : [];

      const firstAlertFocusable = alertFocusableElements[0] ?? closeBtn;
      const lastAlertFocusable =
        alertFocusableElements[alertFocusableElements.length - 1] ?? closeBtn;

      const isInsideAlert =
        !!snackbarRoot && !!active && snackbarRoot.contains(active);
      const isOnTrigger =
        !!lastFocusedRef.current && active === lastFocusedRef.current;

      const nextAfterTrigger =
        !!lastFocusedRef.current && lastFocusedRef.current.isConnected
          ? getNextFocusableOutsideRoot(lastFocusedRef.current, snackbarRoot)
          : null;
      const isOnNextAfterTrigger =
        !!nextAfterTrigger && active === nextAfterTrigger;

      if (!firstAlertFocusable) {
        return;
      }

      if (
        !hasRedirectedFromBodyRef.current &&
        (active === document.body || active === null) &&
        !event.shiftKey
      ) {
        // First Tab after alert opened with focus lost to body → enter alert.
        event.preventDefault();
        hasRedirectedFromBodyRef.current = true;
        firstAlertFocusable.focus();
      } else if (isOnTrigger && !event.shiftKey) {
        // Tab from trigger → first focusable inside alert (virtual insertion).
        event.preventDefault();
        firstAlertFocusable.focus();
      } else if (isInsideAlert && active) {
        const idx = alertFocusableElements.indexOf(active);

        if (idx === -1) {
          return;
        }

        if (!event.shiftKey) {
          // Forward navigation inside alert focus trap.
          if (idx < alertFocusableElements.length - 1) {
            event.preventDefault();
            alertFocusableElements[idx + 1].focus();
          } else if (nextAfterTrigger) {
            // Leaving alert: continue as if alert focusables were inserted
            // immediately after the trigger.
            event.preventDefault();
            nextAfterTrigger.focus();
          }
        } else {
          // Backward navigation inside alert focus trap.
          if (idx > 0) {
            event.preventDefault();
            alertFocusableElements[idx - 1].focus();
          } else if (lastFocusedRef.current) {
            // Shift+Tab from first alert element goes back to trigger.
            event.preventDefault();
            lastFocusedRef.current.focus();
          }
        }
      } else if (event.shiftKey && isOnNextAfterTrigger && lastAlertFocusable) {
        // Shift+Tab from first element after trigger should re-enter alert
        // from its last focusable element.
        event.preventDefault();
        lastAlertFocusable.focus();
      }
    };

    // Capture phase: fires before React delegation and before browser default,
    // ensuring document.activeElement is still the currently focused element.
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [messages.length]);

  const [firstMessage] = messages;
  const severity = firstMessage?.severity || 'success';
  const error = firstMessage?.error;

  const customError: CustomError | undefined =
    error?.name === CustomErrorName.CustomError ||
    error?.name === CustomErrorName.GitRepositoryError
      ? (error as CustomError)
      : undefined;

  const coordinatorError: CoordinatorError | undefined =
    error?.name === CustomErrorName.CoordinatorError
      ? (error as CoordinatorError)
      : undefined;

  const errorMessage: string | undefined =
    customError?.message || coordinatorError?.message || error?.message;

  const submessage: string =
    customError?.errorMessage ||
    coordinatorError?.moreInfo?.solutions[0] ||
    coordinatorError?.moreInfo?.problems[0] ||
    '';

  const handleClose = useCallback(() => {
    if (!firstMessage) {
      return;
    }

    const hasMoreMessages = messages.length > 1;
    setDetailsOpen(false);
    setMessages(msgs => msgs.filter(msg => msg !== firstMessage));

    if (!hasMoreMessages) {
      // Caso 4: last alert closed → restore focus to the original trigger
      lastFocusedRef.current?.focus();
    } else {
      // More alerts remain → focus the close button of the next visible alert.
      // setTimeout ensures the focus call happens after React re-renders the new message.
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 0);
    }
  }, [firstMessage, messages.length]);

  useEffect(() => {
    if (!firstMessage) {
      return undefined;
    }

    if (!(firstMessage.timeoutMillis || severity === 'success')) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleClose();
    }, firstMessage.timeoutMillis ?? 20000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [firstMessage, handleClose, severity]);

  if (!firstMessage) {
    return null;
  }

  return (
    <Snackbar
      ref={snackbarRootRef}
      open
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      className={classes.snackbar}
    >
      {handleMultipleAlerts(
        <Alert
          className={classes.alert}
          action={
            <IconButton
              ref={closeButtonRef}
              style={{ alignSelf: 'start' }}
              color="inherit"
              size="small"
              onClick={handleClose}
              data-testid="error-button-close"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          }
          severity={severity}
        >
          <AlertTitle className={classes.title}>
            {capitalize(
              errorMessage || firstMessage.message?.toString() || severity,
            )}
            {firstMessage.actions && (
              <Box onClick={handleClose}>{firstMessage.actions}</Box>
            )}
          </AlertTitle>
          {!!(customError || coordinatorError) && (
            <div className={classes.alertContent}>
              <span className={classes.subMessage}>
                {capitalize(submessage)}
              </span>

              {!!customError?.moreInfo && (
                <>
                  <WbButton
                    className={classes.detailsLink}
                    onClick={() =>
                      moreInfoCustomError
                        ? setMoreInfoCustomError(undefined)
                        : setMoreInfoCustomError(customError.moreInfo)
                    }
                    label={
                      <Typography>
                        {moreInfoCustomError ? 'Show less' : 'Show more'}
                      </Typography>
                    }
                    variant="text"
                  />

                  {moreInfoCustomError && (
                    <Typography
                      className={classes.moreInfoError}
                      variant="body2"
                    >
                      {moreInfoCustomError}
                    </Typography>
                  )}
                </>
              )}

              {!!coordinatorError && (
                <>
                  <WbButton
                    onClick={() => setDetailsOpen(!detailsOpen)}
                    label={<Typography>Show more</Typography>}
                    variant="text"
                  />
                  <CustomAlertDetails
                    open={detailsOpen}
                    errorInfo={coordinatorError}
                    onClose={() => setDetailsOpen(false)}
                  />
                </>
              )}
            </div>
          )}
        </Alert>,
        messages.length,
      )}
    </Snackbar>
  );
}
